import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { flushSync } from "react-dom"
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge as rfAddEdge
} from "@xyflow/react"
import StepNode from "./StepNode.jsx"
import EdgeWithCounts from "./EdgeWithCounts.jsx"
import ConfigPanel from "./ConfigPanel.jsx"
import { autoLayout } from "./autoLayout.js"
import { configComplete, configSummary, configExtras } from "./configMeta.js"
import usePipelineCounters from "./usePipelineCounters.js"

const NODE_TYPES = { gtmStep: StepNode }
const EDGE_TYPES = { gtmEdge: EdgeWithCounts }

// Edits are buffered locally and flushed in a single batch on Save. The
// initialGraph ref is the baseline we diff against; on a successful save we
// refresh it to the new canonical state so subsequent saves only carry
// further changes. Navigating away mid-edit discards everything because
// nothing was persisted.
export default function PipelineCanvas({
  graph,
  editable,
  graphUrl,
  stepTypes,
  configOptions,
  csrfToken
}) {
  const laidOut = useMemo(() => autoLayout(graph.nodes, graph.edges), [graph])
  const [nodes, setNodes] = useState(laidOut)
  const [edges, setEdges] = useState(graph.edges)
  const [selectedNodeId, setSelectedNodeId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const initial = useRef({ nodes: laidOut, edges: graph.edges })
  // Set true after a successful save-and-leave so the beforeunload handler
  // doesn't warn about state that React hasn't re-rendered with yet.
  const skipUnloadWarning = useRef(false)

  // Seed live edge counts from the server-rendered snapshot so first paint
  // is accurate. The hook keeps them current via ActionCable. Input cards
  // render inputSummary (a static count of segment members) directly from
  // node data — no cable involved.
  const initialEdgeCounts = useMemo(
    () => Object.fromEntries(
      graph.edges
        .filter(e => e.data?.edgeId)
        .map(e => [e.data.edgeId, e.data.counts ?? {}])
    ),
    [graph]
  )
  // No live counts while editing — edge counts are irrelevant during graph
  // mutations and the websocket churn is wasted. Passing null keeps the
  // hook's subscription dormant.
  const edgeCounts = usePipelineCounters(editable ? null : graph.id, initialEdgeCounts)

  const renderedNodes = nodes
  const renderedEdges = useMemo(
    () => edges.map(e => {
      if (editable) return { ...e, data: { ...e.data, counts: undefined } }
      const edgeId = e.data?.edgeId
      const fresh = edgeId ? edgeCounts[edgeId] : null
      if (!fresh) return e
      return { ...e, data: { ...e.data, counts: fresh } }
    }),
    [edges, edgeCounts, editable]
  )

  const jsonHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": csrfToken
    }),
    [csrfToken]
  )

  const stepTypeLabel = useCallback(token => (
    stepTypes?.find(t => t.type === token)?.label ?? token
  ), [stepTypes])

  const stepTypeKind = useCallback(token => (
    stepTypes?.find(t => t.type === token)?.kind ?? "processor"
  ), [stepTypes])

  const onNodesChange = useCallback(changes => {
    setNodes(curr => applyNodeChanges(changes, curr))
  }, [])

  const onEdgesChange = useCallback(changes => (
    setEdges(curr => applyEdgeChanges(changes, curr))
  ), [])

  // Add a step locally with a tmp id; the real id is assigned on Save.
  const addStep = useCallback(type => {
    const tmpId = `tmp-${crypto.randomUUID()}`
    const kind = stepTypeKind(type)
    setNodes(curr => [...curr, {
      id: `step-${tmpId}`,
      type: "gtmStep",
      position: { x: 80 + Math.round(Math.random() * 80), y: 80 + Math.round(Math.random() * 80) },
      data: {
        tmpId,
        stepType: type,
        stepTypeLabel: stepTypeLabel(type),
        name: stepTypeLabel(type),
        config: {},
        configSummary: null,
        configComplete: false,
        configExtras: [],
        inputSummary: null,
        kind
      }
    }])
  }, [stepTypeKind, stepTypeLabel])

  useEffect(() => {
    if (!editable) return
    const handler = e => { if (e.detail?.type) addStep(e.detail.type) }
    window.addEventListener("gtm:add-step", handler)
    return () => window.removeEventListener("gtm:add-step", handler)
  }, [editable, addStep])

  // User drew an edge between two nodes → add to local state with a tmp id.
  const onConnect = useCallback(connection => {
    const tmpId = `tmp-${crypto.randomUUID()}`
    setEdges(curr => rfAddEdge({
      id: `edge-${tmpId}`,
      type: "gtmEdge",
      source: connection.source,
      target: connection.target,
      label: null,
      // No edgeId yet (assigned on Save); counts stay empty until then.
      data: { fromRoute: "default", counts: {} }
    }, curr))
  }, [])

  const onNodeClick = useCallback((_event, node) => {
    if (!editable) return
    setSelectedNodeId(node.id)
  }, [editable])

  const onPaneClick = useCallback(() => setSelectedNodeId(null), [])

  // ConfigPanel "OK" → merge new config into the node's data, recompute
  // local summary/completeness, close the panel. flushSync so that a
  // header Save triggered via gtm:flush-config sees the new node in the
  // same tick rather than the next render.
  const onConfigCommit = useCallback((nodeId, nextConfig) => {
    flushSync(() => {
      setNodes(curr => curr.map(n => {
        if (n.id !== nodeId) return n
        return {
          ...n,
          data: {
            ...n.data,
            config: nextConfig,
            configSummary: configSummary(n.data.stepType, nextConfig, configOptions),
            configComplete: configComplete(n.data.stepType, nextConfig),
            configExtras: configExtras(n.data.stepType, nextConfig, configOptions)
          }
        }
      }))
      setSelectedNodeId(null)
    })
  }, [configOptions])

  const selectedNode = useMemo(
    () => nodes.find(n => n.id === selectedNodeId) ?? null,
    [nodes, selectedNodeId]
  )

  // React Flow already removes deleted nodes/edges from state via
  // onNodesChange / onEdgesChange. The diff on Save tells the server
  // about any non-tmp ids that are no longer present.
  const onNodesDelete = useCallback(() => {}, [])
  const onEdgesDelete = useCallback(() => {}, [])

  const computeDiff = useCallback(() => {
    const initialNodes = initial.current.nodes
    const initialEdges = initial.current.edges
    const initialNodeById = new Map(initialNodes.map(n => [n.id, n]))
    const currentNodeIds = new Set(nodes.map(n => n.id))
    const currentEdgeIds = new Set(edges.map(e => e.id))

    const createdSteps = nodes
      .filter(n => n.data?.tmpId)
      .map(n => ({
        tmp_id: n.data.tmpId,
        type: n.data.stepType,
        name: n.data.name,
        config: n.data.config ?? {},
        position: roundPosition(n.position)
      }))

    const updatedSteps = nodes
      .filter(n => !n.data?.tmpId)
      .filter(n => {
        const orig = initialNodeById.get(n.id)
        return orig && (configChanged(orig, n) || positionChanged(orig, n))
      })
      .map(n => ({
        id: Number(n.data.stepId),
        config: n.data.config ?? {},
        position: roundPosition(n.position)
      }))

    const deletedStepIds = initialNodes
      .filter(n => !currentNodeIds.has(n.id))
      .map(n => Number(n.data.stepId))

    const createdEdges = edges
      .filter(e => isTmpEdgeId(e.id))
      .map(e => ({
        tmp_id: stripPrefix(e.id, "edge-"),
        from_step_id: edgeEndpointId(e.source, nodes),
        to_step_id: edgeEndpointId(e.target, nodes),
        from_route: e.data?.fromRoute || "default"
      }))

    const deletedEdgeIds = initialEdges
      .filter(e => !currentEdgeIds.has(e.id))
      .map(e => Number(stripPrefix(e.id, "edge-")))

    return {
      steps: { created: createdSteps, updated: updatedSteps, deleted: deletedStepIds },
      edges: { created: createdEdges, deleted: deletedEdgeIds }
    }
  }, [nodes, edges])

  const isDirty = useCallback(() => {
    const d = computeDiff()
    return d.steps.created.length || d.steps.updated.length || d.steps.deleted.length ||
           d.edges.created.length || d.edges.deleted.length
  }, [computeDiff])

  // POST the diff. On success, rewrite tmp ids in local state to the real
  // ones the server assigned, then refresh the baseline so future saves
  // are diffed from this point.
  const save = useCallback(async () => {
    setSaving(true)
    setSaveError(null)
    try {
      const diff = computeDiff()
      const res = await fetch(graphUrl, {
        method: "PATCH",
        headers: jsonHeaders,
        body: JSON.stringify(diff)
      })
      if (!res.ok) {
        setSaveError("Save failed.")
        return false
      }
      const { tmp_step_ids = {}, tmp_edge_ids = {} } = await res.json()

      // Map tmp React-Flow node ids → real ones so edge endpoints stay in sync.
      const oldToNewNodeId = {}
      const adoptedNodes = nodes.map(n => {
        if (!n.data?.tmpId) return n
        const realId = tmp_step_ids[n.data.tmpId]
        if (!realId) return n
        const newRfId = `step-${realId}`
        oldToNewNodeId[n.id] = newRfId
        return {
          ...n,
          id: newRfId,
          data: { ...n.data, stepId: realId, tmpId: undefined }
        }
      })

      const adoptedEdges = edges.map(e => {
        let next = e
        if (oldToNewNodeId[e.source]) next = { ...next, source: oldToNewNodeId[e.source] }
        if (oldToNewNodeId[e.target]) next = { ...next, target: oldToNewNodeId[e.target] }
        if (isTmpEdgeId(e.id)) {
          const realId = tmp_edge_ids[stripPrefix(e.id, "edge-")]
          if (realId) next = { ...next, id: `edge-${realId}` }
        }
        return next
      })

      setNodes(adoptedNodes)
      setEdges(adoptedEdges)
      initial.current = { nodes: adoptedNodes, edges: adoptedEdges }
      return true
    } finally {
      setSaving(false)
    }
  }, [computeDiff, graphUrl, jsonHeaders, nodes, edges])

  // Always call the latest `save` — the handler closure below may be the
  // one from a prior render, but the ref is refreshed every render so the
  // post-flushSync `save` (with fresh nodes) is what fires.
  const saveRef = useRef(save)
  saveRef.current = save

  // "Save" link in the page header dispatches this — flush the diff,
  // then leave on success.
  useEffect(() => {
    if (!editable) return
    const handler = async e => {
      const href = e.detail?.href
      if (!href) return
      // Auto-OK any open ConfigPanel so its draft is included in the save.
      // Listeners run synchronously; onConfigCommit uses flushSync, so by
      // the time this returns the node config is already in state and the
      // saveRef points at the new save closure (saveRef is refreshed during
      // render, which flushSync forces synchronously).
      window.dispatchEvent(new CustomEvent("gtm:flush-config"))
      const ok = await saveRef.current()
      if (ok) {
        skipUnloadWarning.current = true
        window.location.href = href
      }
    }
    window.addEventListener("gtm:save-and-leave", handler)
    return () => window.removeEventListener("gtm:save-and-leave", handler)
  }, [editable, save])

  // Warn before navigating away with unsaved changes — covers tab close
  // and the back/forward navigations. The Save link bypasses this by
  // dispatching its own save-and-leave event that runs the batch save
  // (after which there are no unsaved changes).
  useEffect(() => {
    if (!editable) return
    const handler = e => {
      if (skipUnloadWarning.current) return
      if (!isDirty()) return
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [editable, isDirty])

  return (
    <div className="relative h-full w-full">
      <ReactFlow
        nodes={renderedNodes}
        edges={renderedEdges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={editable ? onConnect : undefined}
        onNodesDelete={editable ? onNodesDelete : undefined}
        onEdgesDelete={editable ? onEdgesDelete : undefined}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodesDraggable={editable}
        nodesConnectable={editable}
        elementsSelectable={editable}
        fitView
        fitViewOptions={{ maxZoom: 1 }}
        snapToGrid
        snapGrid={[20, 20]}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={20} size={1} />
        <MiniMap pannable zoomable />
        <Controls showInteractive={false} />
      </ReactFlow>
      {editable && selectedNode && (
        <ConfigPanel
          node={selectedNode}
          options={configOptions}
          onClose={() => setSelectedNodeId(null)}
          onCommit={nextConfig => onConfigCommit(selectedNode.id, nextConfig)}
        />
      )}
      {saving && <div className="absolute top-3 right-3 text-xs text-muted-foreground bg-card border border-border rounded px-2 py-1">Saving…</div>}
      {saveError && <div className="absolute top-3 right-3 text-xs text-destructive bg-card border border-destructive/40 rounded px-2 py-1">{saveError}</div>}
    </div>
  )
}

function isTmpEdgeId(id) {
  return String(id).startsWith("edge-tmp-")
}

function stripPrefix(id, prefix) {
  return String(id).startsWith(prefix) ? String(id).slice(prefix.length) : String(id)
}

function roundPosition(position) {
  if (!position) return undefined
  return { x: Math.round(position.x), y: Math.round(position.y) }
}

function configChanged(a, b) {
  return JSON.stringify(a.data?.config ?? {}) !== JSON.stringify(b.data?.config ?? {})
}

function positionChanged(a, b) {
  const ap = a.position ?? {}, bp = b.position ?? {}
  return Math.round(ap.x ?? 0) !== Math.round(bp.x ?? 0) ||
         Math.round(ap.y ?? 0) !== Math.round(bp.y ?? 0)
}

function edgeEndpointId(reactFlowEndpoint, nodes) {
  // Endpoints are React-Flow node ids like "step-42" or "step-tmp-abc-…".
  // For real steps the server expects the integer id; for tmp steps it
  // expects the tmp_id string (the server resolves it against this same
  // batch).
  const node = nodes.find(n => n.id === reactFlowEndpoint)
  if (node?.data?.tmpId) return node.data.tmpId
  return Number(stripPrefix(reactFlowEndpoint, "step-"))
}
